import React from 'react';
import i18n from 'i18next';
import {
  BenefitGroupsIcon,
  BenefitsIcon,
  ChartIcon,
  CompanyIcon,
  CurrencyIcon,
  EmployeesIcon,
  HomeIcon,
  ReportIcon,
  SupportIcon,
  TransferIcon,
  VoucherIcon
} from 'edenred-ui';
import { SystemRoles } from '@epi-constants/roles';
import { routerPaths } from '@epi-constants/routerPaths';
import config from '@epi-config';

interface UserRoles {
  roles: string[];
}

interface MenuItemProps {
  roles: string[];
  hasOrders?: boolean;
  showVoucherOrder?: boolean;
  urlWithLocale: (path: string) => string;
}

export interface MenuItem {
  name: string;
  link: string;
  icon: React.ReactChild;
  id: string;
  exact?: boolean;
}

interface MenuItemDefinition {
  canDisplay: (props: Omit<MenuItemProps, 'urlWithLocale'>) => boolean;
  createItem: (props: Pick<MenuItemProps, 'urlWithLocale'>) => MenuItem;
}

const { enableNewBenefitGroups } = config;

export const isSignatory = ({ roles }: UserRoles): boolean =>
  roles.includes(SystemRoles.Signatory);

export const isVoucherManager = ({ roles }: UserRoles): boolean =>
  roles.includes(SystemRoles.VoucherManager);

const isSignatoryWithOrders = ({ roles }: { roles: string[] }): boolean =>
  isSignatory({ roles });

const menuItems: MenuItemDefinition[] = [
  {
    canDisplay: isSignatory,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.home'),
      link: urlWithLocale(routerPaths.home),
      icon: <HomeIcon />,
      id: 'navbar-home',
      exact: true
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.manage_benefits'),
      link: urlWithLocale(routerPaths.manageBenefits),
      icon: <BenefitsIcon />,
      id: 'navbar-manage-benefits'
    })
  },
  {
    canDisplay: ({ roles }: UserRoles) =>
      isSignatory({ roles }) && enableNewBenefitGroups,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.benefit_groups'),
      link: urlWithLocale(routerPaths.benefitGroups),
      icon: <BenefitGroupsIcon />,
      id: 'navbar-benefit-groups'
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.beneficiaries'),
      link: urlWithLocale(routerPaths.beneficiaries),
      icon: <EmployeesIcon />,
      id: 'navbar-beneficiaries'
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.deposit_money'),
      link: urlWithLocale(routerPaths.loadCompanyAccount),
      icon: <TransferIcon />,
      id: 'navbar-deposit-money'
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.load_benefit'),
      link: urlWithLocale(routerPaths.loadBenefit),
      icon: <CurrencyIcon />,
      id: 'navbar-load-benefit'
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.reports'),
      link: urlWithLocale(routerPaths.reports),
      icon: <ChartIcon />,
      id: 'navbar-reports'
    })
  },
  {
    canDisplay: isSignatoryWithOrders,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.download_reports'),
      link: urlWithLocale(routerPaths.downloadReports),
      icon: <ReportIcon />,
      id: 'navbar-download-reports'
    })
  },
  {
    canDisplay: ({ roles, showVoucherOrder = false }) =>
      isVoucherManager({ roles }) && showVoucherOrder,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.voucher_order_history'),
      link: urlWithLocale(routerPaths.orderHistory),
      icon: <VoucherIcon />,
      id: 'navbar-voucher-order-history'
    })
  },
  {
    canDisplay: isVoucherManager,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.company_information'),
      link: urlWithLocale(routerPaths.companyInformation),
      icon: <CompanyIcon />,
      id: 'navbar-company-info'
    })
  },
  {
    canDisplay: () => true,
    createItem: ({ urlWithLocale }) => ({
      name: i18n.t('navbar.help'),
      link: urlWithLocale(routerPaths.help),
      icon: <SupportIcon />,
      id: 'navbar-help'
    })
  }
];

export function createMenuItems({
  roles,
  hasOrders,
  urlWithLocale,
  showVoucherOrder
}: MenuItemProps): MenuItem[] {
  return menuItems
    .filter(menuItem =>
      menuItem.canDisplay({ roles, hasOrders, showVoucherOrder })
    )
    .map(menuItem => menuItem.createItem({ urlWithLocale }));
}
